import React from 'react';
import {
  DocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface UploadedFileChipProps {
  file: File;
  onRemove: () => void;
}

const UploadedFileChip: React.FC<UploadedFileChipProps> = ({ file, onRemove }) => {
  const getFileIcon = (type: string, name: string) => {
    if (type.includes('pdf')) return <DocumentIcon className="w-4 h-4 text-red-500" />;
    if (type.includes('word')) return <DocumentTextIcon className="w-4 h-4 text-blue-500" />;
    if (type.includes('text') || name.toLowerCase().endsWith('.txt')) {
      return <DocumentTextIcon className="w-4 h-4 text-gray-500" />;
    }
    if (type.includes('email') || type.includes('rfc822') || name.toLowerCase().endsWith('.eml')) {
      return <EnvelopeIcon className="w-4 h-4 text-green-500" />;
    }
    if (type.includes('image')) return <PhotoIcon className="w-4 h-4 text-purple-500" />;
    return <DocumentIcon className="w-4 h-4 text-gray-500" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
      {/* File Icon */}
      <div className="flex-shrink-0">
        {getFileIcon(file.type, file.name)}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate" title={file.name}>
          {file.name}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatFileSize(file.size)}
        </p>
      </div>

      {/* Remove Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="flex-shrink-0 p-1 text-muted-foreground hover:text-destructive rounded transition-colors"
        aria-label="Remove file"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

export default UploadedFileChip;
